import type {
  InventoryLocationBuilding,
  InventoryMeter,
  JobStatus,
  SerializedInventoryLocation,
  SerializedInventoryMeter,
  SerializedJob,
  Template,
  OutputTechnology,
  MergeInventoryMeterDto,
  SerializedInventoryMeterReadingGroup,
  SerializedHardware,
} from '@enmon/commons/lib/tools';
import { BASE_REST_API_URL } from 'common/constants';
import qs from 'qs';
import type { InventoryMeterFilterActivationStatus } from 'routes/portfolio/locations/const';
import { isNil } from 'ramda';
import { convertSortToEnmonFormat } from '../../utils/convertSortToEnmonFormat';
import type { CountableApiResponse, SerializedMeter } from '@enmon/commons/lib/enmon';
import type { Dayjs } from 'dayjs';
import type { Commodity } from '@enmon/commons/lib/shared';
import { BaseApiClient } from '../baseApiClient';
import type { InventoryMeterFormValues } from '../../../routes/portfolio/meters/routes/detail/routes/details/types';
import type { MeterFilter as BackendGatewayMetersFilter } from '../../../types/backendGateway/metersApi';

export interface MetersFilter {
  current?: number;
  id?: [string | number];
  ids?: (string | number)[];
  buildings?: InventoryLocationBuilding['id'][];
  commodities?: readonly Commodity[];
  serialNumber?: [string];
  inventoryLocation?: SerializedInventoryLocation['id'];
  sort?: string;
  submittedAt?: [Dayjs, Dayjs];
  submittedBy?: InventoryMeter['submitted_by'][];
  locations?: SerializedInventoryLocation['id'][];
  active?: InventoryMeterFilterActivationStatus;
  limit?: number;
  fields?: Array<keyof InventoryMeter>;
  readingGroups?: SerializedInventoryMeterReadingGroup['id'][];
  assignedHardwares?: SerializedHardware['id'][];
}

export type GetCsvExportArg = MetersFilter;

export type GetPickedHardwareCsvExportArg = MetersFilter;

export interface PostPasportArg {
  meterIds: SerializedMeter['toolsId'][];
  templateName?: Template['name'];
}

export interface PostTechnicalSolutionArg {
  meterIds: SerializedMeter['toolsId'][];
  forceMountTechnology?: OutputTechnology;
  replaceExpiredMeters?: boolean;
}

export interface SetActiveHardwareVariantArg {
  inventoryMeterId: SerializedInventoryMeter['id'];
  hardwareVariantId: number;
}

export type GetHierarchyMetersArg = MetersFilter & BackendGatewayMetersFilter;
export type GetHierarchyMetersResponse = CountableApiResponse<SerializedMeter[]>;

export type GetInventoryMeterResponse = SerializedInventoryMeter;

export type GetInventoryMetersArg = MetersFilter & {
  populate?: (keyof SerializedInventoryMeter)[];
};
export type GetInventoryMetersResponse = SerializedInventoryMeter[];

export interface UpdateInventoryMeterArgs {
  id: SerializedInventoryMeter['id'];
  data: UpdateInventoryMeterDto;
}

export type GetCsvExportResponse = string;
export type PostPasportResponse = {
  status: JobStatus;
  job: SerializedJob;
};
export type SetActiveHardwareVariantResponse = void;

export type UpdateInventoryMeterDto = InventoryMeterFormValues;

export class MetersApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('inventory-meters', BASE_REST_API_URL, jwt);
  }

  private static getMeterFilters(filter: MetersFilter): Record<string, unknown> {
    const { id, ids, buildings, serialNumber, commodities, submittedBy, locations, active, inventoryLocation } =
      filter ?? {};

    // TODO: https://enmontech.atlassian.net/browse/ENMON-1896 implement using new queryBuilder
    const filters: Record<string, unknown> = {};

    if (inventoryLocation) {
      filters.inventory_location = {
        id: {
          $eq: inventoryLocation,
        },
      };
    }

    if (locations) {
      filters.inventory_location = {
        id: {
          $in: locations,
        },
      };
    }

    if (id) {
      filters.id = {
        $eq: id,
      };
    } else if (ids?.length) {
      filters.id = {
        $in: ids,
      };
    }

    if (serialNumber) {
      filters.serial_number = {
        $containsi: serialNumber,
      };
    }

    if (buildings) {
      const nonNullBuildings = buildings.filter(building => building !== null);

      filters.inventory_location_building = {
        id: {
          ...(nonNullBuildings.length && { $in: nonNullBuildings }),
          ...(buildings.includes(null) && { $null: true }),
        },
      };
    }

    if (commodities) {
      const nonNullCommodities = commodities.filter(commodity => commodity !== null);

      filters.commodity = {
        ...(nonNullCommodities.length && { $in: nonNullCommodities }),
        ...(commodities.includes(null) && { $null: true }),
      };
    }

    if (submittedBy) {
      const nonNullSubmittedBy = submittedBy.filter(subBy => subBy !== null);

      filters.submitted_by = {
        ...(nonNullSubmittedBy.length && { $in: submittedBy }),
        ...(submittedBy.includes(null) && { $null: true }),
      };
    }

    return filters;
  }

  public async getOne(
    id: SerializedInventoryMeter['id'],
    filter: GetInventoryMetersArg,
    signal?: AbortSignal,
  ): Promise<GetInventoryMeterResponse> {
    const { populate, fields } = filter;

    const response = await this.axios.get<GetInventoryMeterResponse>(
      `${id}?${qs.stringify(
        {
          populate: ['inventory_location_building', ...(populate ?? [])],
          fields,
        },
        {
          encodeValuesOnly: true,
        },
      )}`,
      { signal },
    );

    return response.data;
  }

  public async get(filter: GetInventoryMetersArg, signal?: AbortSignal): Promise<GetInventoryMetersResponse> {
    const { populate, limit, fields } = filter;
    const filters = MetersApiClient.getMeterFilters(filter);

    const response = await this.axios.get<GetInventoryMetersResponse>(
      `?${qs.stringify(
        {
          filters,
          populate: ['inventory_location_building', ...(populate ?? [])],
          fields,
          ...(Number.isInteger(limit) && { pagination: { limit } }),
        },
        {
          encodeValuesOnly: true,
        },
      )}`,
      { signal },
    );

    return response.data;
  }

  public async update({ id, data }: UpdateInventoryMeterArgs): Promise<SerializedInventoryMeter> {
    const response = await this.axios.put<SerializedInventoryMeter>(`/${id}`, { data });

    return response.data;
  }

  public getCsvExport(filter: GetCsvExportArg): string {
    const filters = MetersApiClient.getMeterFilters(filter);

    return `${this.axios.defaults.baseURL}/csv?${qs.stringify(
      { filters, token: this.authToken },
      {
        encodeValuesOnly: true,
      },
    )}`;
  }

  public getPickedHardwareCsvExport(filter: GetPickedHardwareCsvExportArg): string {
    const filters = MetersApiClient.getMeterFilters(filter);

    const url = new URL(`${this.axios.defaults.baseURL}/picked-hardware/csv`);

    url.search = qs.stringify(
      { filters, token: this.authToken },
      {
        encodeValuesOnly: true,
      },
    );

    return url.toString();
  }

  public async setActiveHardwareVariant(data: SetActiveHardwareVariantArg): Promise<SetActiveHardwareVariantResponse> {
    return await this.axios.put('hardware-variant', { data });
  }

  public async postPasport({ meterIds, templateName }: PostPasportArg): Promise<PostPasportResponse> {
    const filters: Record<string, unknown> = {
      id: {
        $in: meterIds,
      },
    };

    const response = await this.axios.post<PostPasportResponse>(`/pasport`, undefined, {
      params: {
        filters,
        template: templateName,
      },
    });

    return response.data;
  }

  public async postTechnicalSolution(body: PostTechnicalSolutionArg) {
    const response = await this.axios.post(`/technical-solution`, body);

    return response.data;
  }

  public async getHierarchyMeters(
    filter: GetHierarchyMetersArg,
    signal?: AbortSignal,
  ): Promise<GetHierarchyMetersResponse> {
    const { sort, submittedAt, active, ...restFilter } = filter;
    const sorts = convertSortToEnmonFormat(sort);

    const response = await this.axios.get<GetHierarchyMetersResponse>('hierarchy', {
      params: {
        ...restFilter,
        ...(!isNil(active) && { active: Boolean(active) }),
        ...(submittedAt && { submittedAt: submittedAt.map(value => value.toISOString()) }),
        ...(sorts && { sorts: sorts.map(({ field, order }) => `${order === 'descend' ? '-' : ''}${field}`).join(',') }),
      },
      signal,
    });

    return response.data;
  }

  async mergeMeters(dto: MergeInventoryMeterDto) {
    const response = await this.axios.post<void>(`merge`, dto);

    return response.data;
  }
}
